/**
 * STORYBOARD UI - Videos Module
 * Handles the VIDEOS tab: video grid, playback, downloads
 *
 * Extracted from ui.js as part of Phase 2 refactoring
 */

import {
  getSceneVideos,
  deleteSceneVideo,
  setCurrentVideo,
  deleteVideoFromHistory
} from '../state.js';
import { downloadVideo } from '../../../../js/core/download.js';

// ============================================================================
// VIDEOS VIEW (Generated Scene Videos)
// ============================================================================

// Callback for refreshing scene card (set by main ui.js)
let refreshSceneCardCallback = null;

/**
 * Set the callback for refreshing scene cards
 * Called by main ui.js during initialization
 */
export function setRefreshSceneCardCallback(callback) {
  refreshSceneCardCallback = callback;
}

/**
 * Initialize videos view event handlers
 */
export function initVideosView() {
  // Videos view uses event delegation on the main container
  // Events are handled in handleVideosViewClick
}

/**
 * Render the VIDEOS tab content (top-level tab)
 * Shows all generated scene videos in a grid with playback controls
 * Supports both canvas (inline) and modal container IDs
 */
export function renderVideosTab() {
  // Try canvas ID first, then modal ID
  const container = document.getElementById('canvas-videos-content')
    || document.getElementById('videos-content');
  if (!container) return;

  renderVideosContent(container);
}

/**
 * Legacy function - kept for backwards compatibility
 */
export function renderVideosView() {
  // Try new container first, fall back to legacy
  const container = document.getElementById('videos-content') || document.getElementById('videos-view');
  if (!container) return;

  renderVideosContent(container);
}

/**
 * Render videos content into a container
 * Task 113 Issue #6: Shows ALL videos with current/variant styling
 */
function renderVideosContent(container) {
  if (!container) return;

  const sceneVideos = getSceneVideos();

  if (sceneVideos.length === 0) {
    container.innerHTML = `
      <div class="videos-empty-state">
        <div class="empty-icon">🎬</div>
        <h3>No Videos Yet</h3>
        <p>Generate videos from your scenes in the SCENES tab</p>
        <button class="btn-go-to-scenes" data-action="go-to-scenes">
          GO TO SCENES
        </button>
      </div>`;
    return;
  }

  // Count unique scenes and total videos
  const uniqueScenes = new Set(sceneVideos.map(sv => sv.sceneSeq)).size;
  const totalVideos = sceneVideos.length;
  const hasVariants = totalVideos > uniqueScenes;

  container.innerHTML = `
    <div class="videos-header">
      <h3>Videos (${totalVideos}${hasVariants ? ` / ${uniqueScenes} scenes` : ''})</h3>
    </div>

    <div class="videos-grid videos-grid-compact">
      ${sceneVideos.map(sv => `
        <div class="video-card video-card-compact ${sv.isCurrent ? 'is-current' : 'is-variant'}"
             data-scene="${sv.sceneSeq}"
             data-video-id="${sv.video.id || ''}">
          <div class="video-card-header-compact">
            <span class="scene-badge">S${sv.sceneSeq}</span>
            ${sv.isCurrent ? '<span class="current-badge">●</span>' : '<span class="variant-badge">○</span>'}
          </div>
          <div class="video-card-player-compact">
            <video src="${sv.video.url}"
                   controls
                   preload="metadata"
                   data-scene="${sv.sceneSeq}">
            </video>
          </div>
          <div class="video-card-actions-compact">
            ${!sv.isCurrent ? `
              <button class="btn-video-compact" data-action="use-video"
                      data-scene="${sv.sceneSeq}" data-video-id="${sv.video.id || ''}"
                      title="Use this video">
                USE
              </button>
            ` : ''}
            <button class="btn-video-compact" data-action="preview-video"
                    data-scene="${sv.sceneSeq}" data-video-id="${sv.video.id || ''}"
                    data-url="${sv.video.url}" title="Fullscreen preview">
              ⛶
            </button>
            <button class="btn-video-compact" data-action="download-video"
                    data-url="${sv.video.url}" title="Download">
              ↓
            </button>
            <button class="btn-video-compact danger" data-action="delete-video"
                    data-scene="${sv.sceneSeq}" data-video-id="${sv.video.id || ''}"
                    title="Delete">
              ×
            </button>
          </div>
        </div>
      `).join('')}
    </div>`;

  // Attach event handlers
  attachVideosViewEvents(container);
}

/**
 * Attach click events to videos view
 */
function attachVideosViewEvents(container) {
  container.addEventListener('click', handleVideosViewClick);
}

/**
 * Handle clicks in videos view
 * Task 113 Issue #6: Added use-video and delete-video actions
 */
export function handleVideosViewClick(e) {
  const target = e.target.closest('[data-action]');
  if (!target) return;

  const action = target.dataset.action;
  const sceneSeq = parseInt(target.dataset.scene);
  const videoId = target.dataset.videoId;

  switch (action) {
    case 'play-video':
      playSceneVideo(sceneSeq, videoId);
      break;
    case 'preview-video':
      openSceneVideoPreview(target.dataset.url);
      break;
    case 'download-video':
      downloadSceneVideo(target.dataset.url);
      break;
    case 'use-video':
      handleUseVideo(sceneSeq, videoId);
      break;
    case 'delete-video':
      handleDeleteVideo(sceneSeq, videoId);
      break;
    case 'delete-scene-video':
      // Legacy: delete all videos for a scene
      handleDeleteSceneVideo(sceneSeq);
      break;
    case 'play-all-videos':
      playAllVideosSequentially();
      break;
    case 'go-to-scenes':
      switchToScenesTab();
      break;
  }
}

/**
 * Play/pause toggle for a scene video
 * Task 113: Updated to support videoId for variant selection
 * Task 113: Added play/pause toggle functionality
 */
function playSceneVideo(sceneSeq, videoId) {
  // Find the specific video card by videoId or fall back to scene
  const selector = videoId
    ? `.video-card[data-video-id="${videoId}"] video`
    : `.video-card[data-scene="${sceneSeq}"] video`;
  const video = document.querySelector(selector);

  if (!video) return;

  const overlay = video.nextElementSibling;

  // Toggle play/pause
  if (!video.paused) {
    // Video is playing - pause it
    video.pause();
    if (overlay) {
      overlay.style.display = 'flex';
      overlay.textContent = '▶';
      overlay.classList.remove('is-playing');
    }
    return;
  }

  // Video is paused - play it
  video.play().then(() => {
    if (overlay) {
      overlay.style.display = 'flex';  // Keep visible but show pause icon
      overlay.textContent = '⏸';
      overlay.classList.add('is-playing');
    }
  }).catch(e => {
    console.warn('Autoplay blocked:', e);
    if (overlay) {
      overlay.style.display = 'flex';
      overlay.textContent = '▶';
      overlay.classList.remove('is-playing');
    }
  });

  // Set up event listeners (only once per video)
  if (!video._eventsAttached) {
    video._eventsAttached = true;

    video.addEventListener('ended', () => {
      if (overlay) {
        overlay.style.display = 'flex';
        overlay.textContent = '▶';
        overlay.classList.remove('is-playing');
      }
    });
  }
}

/**
 * Open video in fullscreen preview modal
 * Task 113: Updated to accept URL directly
 */
function openSceneVideoPreview(url) {
  if (url && window.openVideoPreview) {
    window.openVideoPreview(url);
  }
}

/**
 * Set a video as the current one for a scene
 * Task 113 Issue #6
 */
function handleUseVideo(sceneSeq, videoId) {
  if (!sceneSeq || !videoId) return;

  setCurrentVideo(sceneSeq, videoId);
  // Re-render both canvas and modal containers (renderVideosTab handles canvas ID)
  renderVideosTab();
  renderVideosView();
  updateVideoTabCount();

  // Also refresh the scene card in SCENES view
  if (refreshSceneCardCallback) {
    refreshSceneCardCallback(sceneSeq);
  }

  console.log('[Videos] Set video', videoId, 'as current for scene', sceneSeq);
}

/**
 * Delete a specific video from history
 * Task 113 Issue #6
 */
function handleDeleteVideo(sceneSeq, videoId) {
  if (!sceneSeq || !videoId) return;

  if (!confirm(`Delete this video variant from Scene ${sceneSeq}?`)) return;

  deleteVideoFromHistory(sceneSeq, videoId);
  renderVideosView();
  updateVideoTabCount();

  // Also refresh the scene card in SCENES view
  if (refreshSceneCardCallback) {
    refreshSceneCardCallback(sceneSeq);
  }

  console.log('[Videos] Deleted video', videoId, 'from scene', sceneSeq);
}

/**
 * Download a video file using centralized utility with R2 proxy support
 */
async function downloadSceneVideo(url) {
  const filename = `scene-video-${Date.now()}.mp4`;
  await downloadVideo(url, filename);
}

/**
 * Delete video from a scene
 */
function handleDeleteSceneVideo(sceneSeq) {
  if (!confirm(`Delete video for Scene ${sceneSeq}?`)) return;

  deleteSceneVideo(sceneSeq);
  renderVideosView();

  // Update VIDEOS tab count badge
  updateVideoTabCount();

  // Also refresh the scene card in SCENES view
  if (refreshSceneCardCallback) {
    refreshSceneCardCallback(sceneSeq);
  }
}

/**
 * Play all videos in sequence
 */
async function playAllVideosSequentially() {
  const videos = document.querySelectorAll('.videos-grid video');
  for (const video of videos) {
    video.currentTime = 0;
    const overlay = video.nextElementSibling;
    if (overlay) overlay.style.display = 'none';

    try {
      await video.play();
      await new Promise(resolve => {
        video.onended = resolve;
        video.onerror = resolve;
      });
    } catch (e) {
      // Continue to next video
    }
    if (overlay) overlay.style.display = 'flex';
  }
}

/**
 * Switch to VIDEO SCENES tab (top-level tab)
 */
function switchToScenesTab() {
  const scenesTab = document.querySelector('.prompt-tab[data-target="tab-video-scenes"]');
  scenesTab?.click();
}

/**
 * Truncate text with ellipsis
 */
function truncateText(str, len) {
  if (!str) return '';
  return str.length > len ? str.substring(0, len) + '...' : str;
}

/**
 * Format date for display
 */
function formatVideoDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleDateString();
}

/**
 * Update the video tab counter (top-level VIDEOS tab)
 */
export function updateVideoTabCount() {
  const count = getSceneVideos().length;
  const tab = document.querySelector('.prompt-tab[data-target="tab-videos"]');
  if (tab) {
    tab.textContent = count > 0 ? `VIDEOS (${count})` : 'VIDEOS';
  }
}
